/**
 * Money and billing-period arithmetic. Amounts are integer cents (USD). Billing periods are calendar dates
 * (YYYY-MM-DD, UTC) with an EXCLUSIVE end, exactly as in openapi.yaml (Subscription.currentPeriodEnd, LedgerLine).
 */

export function formatCents(cents: number | null | undefined, currency = 'USD'): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

/**
 * Proration for added seats, as the backend computes it (ChangeSeats / LedgerLine.amountCents):
 * round_half_up(added x monthly price x daysUsed / periodDays), in exact integer arithmetic.
 */
export function prorate(addedSeats: number, pricePerVehicleCents: number, daysRemaining: number, daysInPeriod: number): number {
  if (addedSeats <= 0 || daysInPeriod <= 0 || daysRemaining <= 0 || pricePerVehicleCents <= 0) return 0;
  const n = addedSeats * pricePerVehicleCents * daysRemaining;
  return Math.floor((2 * n + daysInPeriod) / (2 * daysInPeriod));
}

const DAY_MS = 86_400_000;
const toUtc = (date: string) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
};

/** Whole days from `from` to `to` (YYYY-MM-DD); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS);
}

/** Today's calendar date in UTC (the backend's default effective date). */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** The subscription fields the estimate needs (a subset of Schemas['Subscription']). */
export interface SeatEstimateInput {
  vehicleSeats: number;
  pricePerVehicleCents: number;
  baseFeeCents: number;
  comp: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
}

export interface SeatChangeEstimate {
  currentSeats: number;
  newSeats: number;
  delta: number;
  /** increase: effective on `effectiveDate`; reduction: at the next period start; none: unchanged. */
  kind: 'increase' | 'reduction' | 'none';
  effectiveDate: string | null;
  daysUsed: number;
  periodDays: number;
  /** Estimated proration charged now (0 for reductions, in trial, or comp). */
  prorationCents: number;
  /** Why there is no proration for an increase, if any. */
  noProrationReason: 'trial' | 'comp' | null;
}

/**
 * Client-side ESTIMATE of a seat change, used while the server dry run (POST .../seats/preview) loads or if it fails.
 * Mirrors the backend rule:
 * increase = added x monthly price x (days from the effective date, inclusive, to the exclusive period end) / period days;
 * no proration before billing starts (trial); reductions apply at the next period start with no refund or credit.
 * Comp subscriptions are invoiced at 0, so the estimate shows 0 for them. The ledger line the backend creates is authoritative.
 */
export function estimateSeatChange(s: SeatEstimateInput, newSeats: number, effectiveDate: string = todayUtc()): SeatChangeEstimate {
  const delta = newSeats - s.vehicleSeats;
  const base = { currentSeats: s.vehicleSeats, newSeats, delta, daysUsed: 0, periodDays: 0, prorationCents: 0, noProrationReason: null };
  if (delta === 0) return { ...base, kind: 'none', effectiveDate: null };
  if (delta < 0) return { ...base, kind: 'reduction', effectiveDate: s.currentPeriodEnd };
  if (!s.currentPeriodStart || !s.currentPeriodEnd) return { ...base, kind: 'increase', effectiveDate, noProrationReason: 'trial' };
  const periodDays = daysBetween(s.currentPeriodStart, s.currentPeriodEnd);
  const daysUsed = Math.max(0, Math.min(periodDays, daysBetween(effectiveDate, s.currentPeriodEnd)));
  const amount = prorate(delta, s.pricePerVehicleCents, daysUsed, periodDays);
  return {
    ...base,
    kind: 'increase',
    effectiveDate,
    daysUsed,
    periodDays,
    prorationCents: s.comp ? 0 : amount,
    noProrationReason: s.comp ? 'comp' : null,
  };
}

/** Recurring charge for one period: base fee + seats x per-vehicle price (0 when comp). */
export function periodChargeCents(s: Pick<SeatEstimateInput, 'baseFeeCents' | 'pricePerVehicleCents' | 'comp'>, seats: number): number {
  return s.comp ? 0 : s.baseFeeCents + seats * s.pricePerVehicleCents;
}
